import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import SystemHelper from 'features/System/systemHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker from 'general/components/Others/CustomDateRangePicker';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

ActionLogListView.propTypes = {
    showFullUI: PropTypes.bool,
    prefKey: PropTypes.string,
};
const defaultColumnIds = [
    'NOTIFICATION_CODE',
    'TITLE',
    'CONTENT',
    'RECEIVER',
    'CREATED_AT',
    'CREATED_BY',
    'LINK',
    'CATEGORY',
    'STATUS',
    'TIME'
];

function ActionLogListView({ showFullUI = false, prefKey }) {
    // MARK: ---- PROPERTIES ----
    const { user } = useSelector((state) => state.auth);
    const adminRole =
        user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
    const [filter, setFilter] = useState(SystemHelper.Filter.actionLogList);
    const [actionLogList, setActionLogList] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        const fetchActionLogs = async () => {
            try {
                setIsLoading(true);
                const params = {
                    ..._.pickBy(filter, value => value !== null && value !== undefined && value !== '')
                };

                const response = await SystemHelper.API.requestGetAuditList(params);
                setActionLogList(response?.audits || []);
                setTotal(response?.total || 0);
            } catch (error) {
                console.error('Error fetching action logs:', error);
                setActionLogList([]);
                setTotal(0);
            } finally {
                setIsLoading(false);
            }
        };

        fetchActionLogs();
    }, [filter]);

    const fullColumns = useMemo(() => {
        return [
            {
                id: 'NOTIFICATION_CODE',
                name: UIHelper.buildTableColumn('Mã nhật ký'),
                selector: (row) => row.auditId,
                width: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'TITLE',
                name: UIHelper.buildTableColumn('Người dùng'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            Utils.openInNewTab(`${AppRoute.customerDetail}/${row?.user?.userId ?? ''}`);
                        }}
                    >
                        {row?.user?.fullname}
                    </span>
                ),
                minWidth: '320px',
                style: tableStyle.columns.link,
                wrap: true,
            },
            {
                id: 'CONTENT',
                name: UIHelper.buildTableColumn('Hành động'),
                selector: (row) => row?.action,
                minWidth: '400px',
                style: tableStyle.columns.normal,
                wrap: true,
            },
            {
                id: 'RECEIVER',
                name: UIHelper.buildTableColumn('Loại đối tượng'),
                selector: (row) => row?.objectType,
                width: '200px',
                style: tableStyle.columns.link,
            },
            {
                id: 'CREATED_AT',
                name: UIHelper.buildTableColumn('Mã đối tượng'),
                selector: (row) => row?.objectId,
                width: '150px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'CREATED_BY',
                name: UIHelper.buildTableColumn('Mô tả'),
                selector: (row) => row?.description,
                minWidth: '200px',
                style: tableStyle.columns.normal,
                wrap: true,
            },
            {
                id: 'LINK',
                name: UIHelper.buildTableColumn('Mã trạng thái'),
                selector: (row) => row?.status,
                minWidth: '250px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Kết quả'),
                selector: (row) => row?.result,
                width: '100px',
            },
            {
                id: 'TIME',
                name: UIHelper.buildTableColumn('Thời gian gửi'),
                selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm'),
                width: '150px',
                style: tableStyle.columns.normal,
            },
        ];
    }, []);
    const [fullColumnIds, setFullColumnIds] = useState(
        fullColumns?.map((row) => row?.id).filter(Boolean) || []
    );
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        onSelectAll,
        onUnSelectAll,
        fullColumnsSorted,
        fullColumnsIdSorted,
        saveChanges,
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    // MARK: ---- METHODS ----
    const handleRefresh = () => {
        setFilter({ ...SystemHelper.Filter.actionLogList });
    };

    return (
        <div>
            {UIHelper.buildCard(
                <div>
                    <div className="py-4">
                        {/* header */}
                        <div className="px-4 d-flex align-items-center justify-content-between">
                            {/* title */}
                            <div className="d-flex flex-column">
                                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                    Nhật ký hoạt động
                                </span>
                                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                    Tổng:{' '}
                                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                                        {Utils.formatNumber(total)} hoạt động
                                    </span>
                                </span>
                            </div>
                        </div>

                        {/* table */}
                        <div>
                            {/* filter */}
                            <div className="row m-0 p-0 px-2">
                                {/* tim kiem */}
                                <div className="col mb-4 px-2">
                                    {UIHelper.buildFormLabel('Tìm kiếm')}
                                    <KTFormSearch
                                        name="search"
                                        placeholder="Theo mã hoạt động, tiêu đề, nội dung..."
                                        value={filter?.q}
                                        onSubmit={(text) => {
                                            SystemHelper.Filter.actionLogList = {
                                                ...SystemHelper.Filter.actionLogList,
                                                q: text,
                                                page: 0,
                                            };
                                            handleRefresh();
                                        }}
                                    />
                                </div>
                                {/* loc theo trang thai */}
                                {/* <div className="col mb-4 px-2">
                                    {UIHelper.buildFormLabel('Trạng thái')}
                                    <KTBSDropdown
                                        name="status"
                                        toggleElement={
                                            <button
                                                className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                                type="button"
                                            >
                                                {_.find(ChargeHelper.Data.notificationStatusOptions, { value: filter?.status })
                                                    ?.name ?? 'Tất cả'}
                                            </button>
                                        }
                                        selectedValue={filter?.status}
                                        dropdownMenuItems={[
                                            { name: 'Tất cả', value: '' },
                                            ...ChargeHelper.Data.notificationStatusOptions,
                                        ]}
                                        dropdownMenuClassName="overflow-auto w-100"
                                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                                        onChange={(value) => {
                                            SystemHelper.Filter.actionLogList = {
                                                ...SystemHelper.Filter.actionLogList,
                                                status: value,
                                                page: 0,
                                            };
                                            handleRefresh();
                                        }}
                                        isDropdownForm={true}
                                    />
                                </div> */}
                                {/* ngay tao */}
                                {/* <div className="col mb-4 px-2">
                                    {UIHelper.buildFormLabel('Ngày tạo')}
                                    <CustomDateRangePicker
                                        toggleElementBorderRadius={5}
                                        initialValue={filter?.dateFilter}
                                        onPressSave={(selectedValue, startDate, endDate) => {
                                            SystemHelper.Filter.actionLogList = {
                                                ...SystemHelper.Filter.actionLogList,
                                                dateFilter: selectedValue,
                                                from: startDate,
                                                to: endDate,
                                                page: 0,
                                            };
                                            handleRefresh();
                                        }}
                                    />
                                </div> */}
                                {/* danh muc */}
                                {/* <div className="col mb-4 px-2">
                                    {UIHelper.buildFormLabel('Danh mục')}
                                    <KTBSDropdown
                                        name="category"
                                        toggleElement={
                                            <button
                                                className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                                                type="button"
                                            >
                                                {_.find(
                                                    ChargeHelper.Data.notificationCategoryOptions,
                                                    (item) => item.value === SystemHelper.Filter.actionLogList?.category
                                                )?.name ?? 'Tất cả'}
                                            </button>
                                        }
                                        selectedValue={SystemHelper.Filter.actionLogList?.category}
                                        dropdownMenuItems={[
                                            { name: 'Tất cả', value: '' },
                                            ...ChargeHelper.Data.notificationCategoryOptions,
                                        ]}
                                        dropdownMenuClassName="overflow-auto w-100"
                                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                                        onChange={(value) => {
                                            SystemHelper.Filter.actionLogList = {
                                                ...SystemHelper.Filter.actionLogList,
                                                category: value,
                                                page: 0,
                                            };
                                            handleRefresh();
                                        }}
                                        isDropdownForm={true}
                                    />
                                </div> */}
                                {/* thiet lap cot */}
                                <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                                    {/* bo loc */}
                                    {/* <ButtonFilterDropdown
                  dropdownContent={
                    <ChargeVehicleListFilterView
                      onCancel={() => {
                        handleRefresh();
                      }}
                      onApply={() => {
                        handleRefresh();
                      }}
                      initialSelectedBrand={selectedVehicleBrand}
                      initialSelectedModel={selectedVehicleModel}
                      onSelectedBrandChange={(value) => {
                        setSelectedVehicleBrand(value);
                      }}
                      onSelectedModelChange={(value) => {
                        setSelectedVehicleModel(value);
                      }}
                    />
                  }
                /> */}
                                    {/* <DropdownTableConfigColumn
                                        columnIds={columnIds}
                                        fullColumns={fullColumns}
                                        fullColumnIds={fullColumnsIdSorted}
                                        onChangeCheckboxValue={onChangeCheckboxValue}
                                        resetDefaultTableColumn={resetDefaultTableColumn}
                                        onChangeFullColumnIds={(ids) => {
                                            setFullColumnIds(ids);
                                            const key = prefKey + '_FULL_COLUMN_IDS';
                                            localStorage.setItem(key, ids.join('|'));
                                        }}
                                        onSelectAll={onSelectAll}
                                        onUnSelectAll={onUnSelectAll}
                                        saveChanges={saveChanges}
                                    /> */}
                                </div>
                            </div>
                            <div className="px-4">
                                <KTTable
                                    columns={columns}
                                    items={actionLogList}
                                    selectableRows={false}
                                    striped={false}
                                    compactGrid={false}
                                    hasActionColumn={false}
                                    onClickedRow={(row) => { }}
                                    onSelectedRowsChanged={(rows) => { }}
                                    onDoubleClickedRow={(row) => { }}
                                    noDataComponent={UIHelper.buildEmptyNoData(
                                        'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                                    )}
                                />
                            </div>
                        </div>

                        {/* pagination */}
                        <div className="px-4 mt-2">
                            <KTPagination
                                currentPage={filter?.page + 1}
                                rowCount={total}
                                rowsPerPage={filter?.limit}
                                currentItemInPageCount={actionLogList?.length}
                                onChangePage={(newPage) => {
                                    const iNewPage = parseInt(newPage);
                                    SystemHelper.Filter.actionLogList = {
                                        ...SystemHelper.Filter.actionLogList,
                                        page: iNewPage - 1,
                                    };
                                    handleRefresh();
                                }}
                                onChangeRowsPerPage={(newRowsPerPage) => {
                                    const iNewRowsPerPage = parseInt(newRowsPerPage);
                                    SystemHelper.Filter.actionLogList = {
                                        ...SystemHelper.Filter.actionLogList,
                                        limit: iNewRowsPerPage,
                                        page: 0,
                                    };
                                    handleRefresh();
                                }}
                                loading={false}
                                color={KTPaginationColor.success}
                                appendText="hoạt động"
                            />
                        </div>
                    </div>
                </div>,
                false,
                !showFullUI
            )}
        </div>
    );
}

export default ActionLogListView;
